import { useMemo } from 'react'

import { RouteLocation, RouteLocationStatus } from 'lib/types/maps'
import Modal from '../Modal/Modal'
import Notification from '../Notification/Notification'
import OrderTrackingMap from '../OrderTracking/OrderTrackingMap'
import { providerIDs } from '../ServiceProviderLogo/ServiceProviderLogo'
import ShipmentDetailsModalHistory from './partials/ShipmentDetailsModalHistory'
import ShipmentDetailsModalInfoList from './partials/ShipmentDetailsModalInfoList'
import ShipmentDetailsModalRoute from './partials/ShipmentDetailsModalRoute'

type ShipmentDetailsModalAddress = Pick<
  OrderAddress,
  'address_1' | 'city' | 'state' | 'zip' | 'country'
> &
  Partial<Pick<OrderAddress, 'address_2' | 'company_name' | 'attention_name'>>

export interface ShipmentDetailsModalUpdate {
  title: string
  date_time?: string
  location?: string
  phone?: string
}

export interface ShipmentDetailsModalProps {
  /**
   * A unique for this modal
   */
  id: string
  /**
   * The modal heading
   */
  name: string
  /**
   * Whether the shipment is a pickup
   */
  pickup?: boolean

  /**
   * Pickup date, preferrably in YYYY-MM-DD format
   */
  pickup_date: string
  /**
   * Delivery date, preferrably in YYYY-MM-DD format
   */
  delivery_by: string
  /**
   * Service name, e.g. Expedited Ground
   */
  service: string
  /**
   * Insurance coverage amount in cents
   */
  insurance_cents: number
  /**
   * Current shipment status
   */
  status: 'pending_shipment' | 'shipped' | 'delayed' | 'cancelled' | 'delivered'
  /**
   * Carrier service handle/ID
   */
  shipper: providerIDs
  /**
   * The origin address
   */
  origin: ShipmentDetailsModalAddress
  /**
   * The destination address
   */
  destination: ShipmentDetailsModalAddress
  /**
   * Whether the origin end is a cruise (ship/port) — true on return (from-cruise)
   * legs. Drives whether cruise details render under the origin.
   */
  originIsCruise?: boolean
  /**
   * Whether the destination end is a cruise (ship/port) — true on outbound
   * (to-cruise) legs. Drives whether cruise details render under the destination.
   */
  destinationIsCruise?: boolean
  /**
   * Cruise session details (cruise line, ship, port, cabin, reservation #,
   * dates). Rendered under the cruise end. Only populated for cruise shipments.
   */
  cruise?: CruiseSession | null
  /**
   * A list of tracking history status updates, in reverse-chronological order
   */
  updates: ShipmentDetailsModalUpdate[]
  /**
   * Optional warning text, e.g., reason for delayed shipment
   */
  warning?: string
  /**
   * Whether the user has permission to view full address details (admin or order owner)
   */
  canViewAddresses?: boolean
  /**
   * Whether the user has permission to view insurance amount (admin or order owner)
   */
  canViewInsurance?: boolean
  /**
   * Whether the leg is an Uber pickup
   */
  uberPickup?: boolean
  /**
   * ISO timestamp for Uber pickup window start
   */
  pickupReadyTime?: string
  /**
   * ISO timestamp for Uber pickup window end
   */
  pickupCloseTime?: string
  /**
   * IANA timezone of the origin address for formatting the pickup window
   */
  originTimeZone?: string
}

const getAddressLine = (point: Address): string => {
  return `${point.city}, ${point.state} ${point.zip} ${point.country}`
}

export default function ShipmentDetailsModal({
  id,
  name,
  pickup = true,
  pickup_date,
  delivery_by,
  service,
  insurance_cents,
  shipper,
  origin,
  destination,
  originIsCruise = false,
  destinationIsCruise = false,
  cruise,
  status,
  updates,
  warning,
  canViewAddresses = false,
  canViewInsurance = false,
  uberPickup,
  pickupReadyTime,
  pickupCloseTime,
  originTimeZone,
}: ShipmentDetailsModalProps) {
  const routeLocations = useMemo(() => {
    if (!origin || !destination) return []

    const locations: RouteLocation[] = []

    locations.push({
      id: 'origin',
      name: 'origin',
      address: getAddressLine(origin as Address),
      type: 'origin',
      status: status as RouteLocationStatus,
    })

    locations.push({
      id: 'destination',
      name: 'destination',
      address: getAddressLine(destination as Address),
      type: 'destination',
      status: status as RouteLocationStatus,
    })

    return locations
  }, [origin, destination, status])

  return (
    <Modal
      id={id}
      className="[&]:md:max-w-[1088px]"
      dialogPanelClass="[&]:md:max-w-[1088px]"
      content={
        <div>
          <h2 className="type-body-1">{name}</h2>
          {warning ? (
            <Notification
              type="urgent"
              title={warning}
              icon={{ icon: 'error' }}
              className="mt-4 lg:mt-8"
            />
          ) : null}
          {/* Map */}
          <div className="w-full bg-white mt-4 lg:mt-8 !min-h-[300px]">
            <OrderTrackingMap
              routeLocations={routeLocations}
              showRoute={true}
            />
          </div>
          {/* General Info */}
          <div className="flex flex-col gap-4 mt-4 lg:mt-8 lg:flex-row lg:flex-nowrap">
            <ShipmentDetailsModalRoute
              shipper={shipper}
              origin={origin}
              destination={destination}
              originIsCruise={originIsCruise}
              destinationIsCruise={destinationIsCruise}
              cruise={cruise}
              canViewAddresses={canViewAddresses}
            />
            <ShipmentDetailsModalInfoList
              pickup={pickup}
              pickup_date={pickup_date}
              delivery_by={delivery_by}
              service={service}
              insurance_cents={insurance_cents}
              canViewInsurance={canViewInsurance}
              uberPickup={uberPickup}
              pickupReadyTime={pickupReadyTime}
              pickupCloseTime={pickupCloseTime}
              originTimeZone={originTimeZone}
            />
          </div>
          <ShipmentDetailsModalHistory status={status} updates={updates} />
        </div>
      }
    />
  )
}
